import 'regenerator-runtime/runtime';
import { call, cancel, select, takeEvery } from 'redux-saga/effects';

import {
  getAuslandsRisikoZuschlag,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  Fachbaustein,
  FahrradWert,
  Feature,
  VhvDatenAntrag,
  VorschadenArt,
} from '@ps20/bff-models';
import {
  App,
  convertToBffLaufzeit,
  DIPLOMATEN_CODE,
  getFachschluessel,
  handleLinieFromVorvertrag,
  mapToBffVorvertragsNummer,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import dayjs from 'dayjs';
import { convertRisikoortToBffAdresse } from '@ps20-frontend/vorschlagsdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { selectFahrradWertOfSelectedLinie } from '../../app/shared/selectors/versicherungssummen.selector';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';
import { IAppState } from '../appState';

const DEFAULT_BETRACHTUNGSZEITRAUM_VHV = 5;
const HAUSRAT = 'HH000173';
const FELS = 'HH000199';
const HHWertsachen19 = 'HH000237';
export const FAHRRAD = 'HH000183';

export function* watchVorschlagdatenChanged() {
  yield takeEvery(GlobalActions.VORSCHLAGDATEN_REQUESTED, safeSaga(vorschlagdatenSaga, App.VHV));
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>) {
  const angebote = action.payload.angebotsIds;
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();

  if (
    !angebote.some(angebot => angebot.appId === App.VHV && angebot.angebotsId === state.angebotsId)
  ) {
    yield cancel();
    return;
  }

  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);

  const vorschlagdaten: VhvDatenAntrag = {
    angebotsId: state.angebotsId,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    spartenType: state.produkt.spartenType,
    jahresbeitrag: state.jahresbeitrag,
    savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
    gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
      state.vertragsoptionen.zahlungsweise,
      state.gesamtbeitragMitRabatt,
      state.jahresbeitrag,
      App.VHV,
    ),
    savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
    antragsId: state.antragsnummer,
    sicherungsglaeubiger: null,
    hspLfnr: state.produkt.hspLfnr,
    wohnung: {
      hausart: state.versObjekt.hausart,
      stockwerk: state.versObjekt.stockwerk,
      staendigBewohnt: state.versObjekt.staendigBewohnt,
      gebaeudeStaendigBewohnt: state.versObjekt.gebaeudeStaendigBewohnt,
      gebaeudeInnerorts: state.versObjekt.gebaeudeInnerhalbWohngebiet,
      adresse: convertRisikoortToBffAdresse(state.risikoort),
      wohnflaecheQm: state.versObjekt.wohnflaeche,
      vorschaedenVorhanden: state.vorschaeden.vorhanden,
      familienStand: state.versicherungsnehmer.familienstand,
      vorschaeden: [
        {
          vorschadenArt: VorschadenArt.RAD,
          anzahl: state.vorschaeden.fahrrad,
          betrachtungszeitraum: DEFAULT_BETRACHTUNGSZEITRAUM_VHV,
        },
        {
          vorschadenArt: VorschadenArt.FELS,
          anzahl: state.vorschaeden.fels,
          betrachtungszeitraum: DEFAULT_BETRACHTUNGSZEITRAUM_VHV,
        },
        {
          vorschadenArt: VorschadenArt.ELEMENTAR,
          anzahl: state.vorschaeden.elementar,
          betrachtungszeitraum: DEFAULT_BETRACHTUNGSZEITRAUM_VHV,
        },
      ],
    },
    produkt: {
      beginn: null,
      ablauf: null,
      selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
      produktline: ausgewaehlteProduktlinie.produktName,
      kurztext: ausgewaehlteProduktlinie.kurztext,
      langtext: ausgewaehlteProduktlinie.langtext,
      fachschluessel: setFachschluessel(state, ausgewaehlteProduktlinie),
    },
    rbdMax: ausgewaehlteProduktlinie.rbdMax,
    auslandsRisikoZuschlag: getAuslandsRisikoZuschlag(
      state.vertragsoptionen?.selectedKooperation === DIPLOMATEN_CODE,
      ausgewaehlteProduktlinie.nachlass,
    ),
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    savedRbdNachlass: state.taasInfo.savedRbdNachlass,
    savedTbNachlass: state.taasInfo.savedTbNachlass,
    vorteilsrabatt: state.taasInfo.vorteilsrabatt,
    preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
    vorversicherung: [],
    antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
    laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
    rabattstufe: state.rabattstufe,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
    vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    zielmarktAbweichung: false,
    kooperation: state.vertragsoptionen.selectedKooperation,
  };

  handleLinieFromVorvertrag(vorschlagdaten, ausgewaehlteProduktlinie, state.vertragsoptionen);

  yield call(globalPut.put, new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten));
}

export function setFachschluessel(
  state: IAppState,
  produktline: Produktlinie,
): Array<Fachbaustein> {
  const fachschluessel = getFachschluessel(state.produkt);

  fachschluessel.forEach(fschl => {
    switch (fschl.fachschluessel) {
      case HAUSRAT: {
        fschl.summe = state.versicherungssummen
          .find(versicherungsumme => versicherungsumme.produktlinie === produktline.produktName)
          .summe.toString();
        break;
      }
      case HHWertsachen19: {
        fschl.summe = state.wertsachensummen
          .find(wertsachensumme => wertsachensumme.produktline === produktline.produktName)
          .summe.toString();
        break;
      }
      case FELS: {
        if (state.vertragsoptionen?.beibehalten) {
          produktline.gefahren
            .filter(gefahr => gefahr.name === 'FELS')
            .forEach(hauptGefahr => {
              fschl.deltafaktor = hauptGefahr.deltafaktor;
              fschl.anzahlVorschaeden = hauptGefahr.anzahlVorschaeden;
              fschl.plzFaktor = hauptGefahr.plzFaktor;
            });
        }
        break;
      }
      case FAHRRAD: {
        fschl.summe = convertToSumme(selectFahrradWertOfSelectedLinie(state));
      }
    }
  });

  return fachschluessel;
}

function convertToSumme(fahrradWert: FahrradWert): string {
  return {
    [FahrradWert.BIS1500]: '1500',
    [FahrradWert.BIS3000]: '3000',
    [FahrradWert.BIS6000]: '6000',
    [FahrradWert.UEBER6000]: '6001',
  }[fahrradWert];
}
